import { Head, usePage } from "@inertiajs/react"
import PublicLayout from "@/components/PublicLayout"
import { TherapistAvatar } from "@/components/ui/therapist-avatar"
import { LocationTag } from "@/components/ui/location-tag"
import type { PageProps } from "@/types/inertia"

interface Therapist {
  id: number
  name: string
  slug: string
  speciality: string
  photo_url: string | null
  sites: { name: string; slug: string }[]
}

interface Props {
  therapists: Therapist[]
}

export default function TherapistsIndex({ therapists }: Props) {
  const { translations } = usePage<PageProps>().props
  const t = translations.therapists.index
  const tc = translations.common

  return (
    <>
      <Head title="Our Therapists — Sanctuary Therapy Centre">
        <meta
          name="description"
          content="Meet the therapists based at Sanctuary Therapy Centre in Prestwich and Hebden Bridge. Find the right practitioner for your needs."
        />
        <meta property="og:title" content="Our Therapists — Sanctuary Therapy Centre" />
        <meta
          property="og:description"
          content="Experienced therapists and counsellors at our Prestwich and Hebden Bridge locations."
        />
      </Head>
      <PublicLayout>
        {/* Page header */}
        <section className="bg-stone pt-32 pb-14">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <h1>{t.title}</h1>
            <p className="mt-3 max-w-xl text-ink-muted">{t.subtitle}</p>
          </div>
        </section>

        {/* Therapist grid */}
        <section className="bg-cream py-14">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            {therapists.length > 0 ? (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {therapists.map((therapist) => (
                  <a
                    key={therapist.id}
                    href={`/therapists/${therapist.slug}`}
                    className="group flex flex-col items-center rounded-2xl bg-white p-6 text-center ring-1 ring-hairline transition-shadow hover:shadow-md"
                  >
                    {therapist.photo_url ? (
                      <img
                        src={therapist.photo_url}
                        alt={therapist.name}
                        className="h-20 w-20 rounded-full object-cover"
                      />
                    ) : (
                      <TherapistAvatar name={therapist.name} size="lg" />
                    )}
                    <h3 className="mt-4 text-base">{therapist.name}</h3>
                    <p className="mt-1 text-sm text-ink-muted">{therapist.speciality}</p>
                    {therapist.sites.length > 0 && (
                      <div className="mt-3 flex flex-wrap justify-center gap-1.5">
                        {therapist.sites.map((site) => (
                          <LocationTag key={site.slug} name={site.name} />
                        ))}
                      </div>
                    )}
                    <span className="mt-4 text-xs font-medium text-teal-mid group-hover:text-teal">
                      {tc.view_profile}
                    </span>
                  </a>
                ))}
              </div>
            ) : (
              <p className="text-center text-ink-muted py-20">{t.coming_soon}</p>
            )}
          </div>
        </section>

        {/* CTA */}
        <section className="bg-stone py-16">
          <div className="mx-auto max-w-3xl px-6 text-center lg:px-8">
            <h2>Ready to get started?</h2>
            <p className="mt-4 text-ink-muted">
              Looking for therapy? Visit Gisela's page to learn more and send an enquiry.
              Looking to rent a room? See our available rooms in Prestwich and Hebden Bridge.
            </p>
            <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <a href="/therapy-with-gisela" className="rounded-full bg-teal px-6 py-2.5 text-sm font-medium text-white transition-opacity hover:opacity-90">
                Therapy with Gisela →
              </a>
              <a href="/rent-a-room" className="rounded-full border border-hairline bg-cream px-6 py-2.5 text-sm font-medium text-ink-body transition-colors hover:bg-stone">
                Rent a room →
              </a>
            </div>
          </div>
        </section>
      </PublicLayout>
    </>
  )
}
